import { LngLat } from "mapbox-gl";
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useMap } from "react-map-gl";
import { useNavigate, useParams } from 'react-router-dom';
import { useContextMenuStore, usePopupStore, usePropertyStore, useViewSettingsStore } from '../..';
import { usePropertyBoeGenerator } from "../../hooks/boeGenerator/usePropertyBoeGenerator";
import useClosable from '../../hooks/useClosable';
import useProperty from "../../hooks/useProperty";
import { useDetailsShapesOnMap } from "../../hooks/useShapesOnMap";
import DetailsType from '../../interfaces/DetailsType';
import { GeoCoordinate } from "../../interfaces/GeoCoordinate";
import IProperty from "../../interfaces/IProperty";
import { ClosableOrder } from '../../store/ClosableStore';
import ButtonWithTooltip from "../ButtonWithTooltip";
import commonStyles from '../Common.module.css';
import detailsStyles from '../Details.module.css';
import DetailsPanelWithFlyTo from "../DetailsPanelWithFlyTo";
import { PropertyFavoriteCheckbox } from "../favorites/FavoriteCheckbox";
import AddLocationIcon from "../icons/AddLocationIcon";
import CompanyIcon from "../icons/CompanyIcon";
import DealIcon from '../icons/DealIcon';
import DetailsIcon from "../icons/DetailsIcon";
import LeadIcon from '../icons/LeadIcon';
import LeaseCompIcon from '../icons/LeaseCompIcon';
import PropertyIcon from '../icons/PropertyIcon';
import SaleCompIcon from '../icons/SaleCompIcon';
import StarsIcon from "../icons/StarsIcon";
import TableConvertIcon from "../icons/TableConvertIcon";
import TenantInMarketIcon from '../icons/TenantInMarketIcon';
import IconWithTooltip from "../IconWithTooltip";
import SegmentedButtons from '../SegmentedButtons';
import OnMapSpinner from "../spinners/OnMapSpinner";
import Spinner from "../spinners/Spinner";
import PropertyDetailsAiGradeTab from "./PropertyDetailsAiGradeTab";
import PropertyDetailsCompaniesTab from "./PropertyDetailsCompaniesTab";
import PropertyDetailsDealsTab from './PropertyDetailsDealsTab';
import PropertyDetailsLeadsTab from './PropertyDetailsLeadsTab';
import PropertyDetailsLeaseCompsTab from './PropertyDetailsLeaseCompsTab';
import PropertyDetailsSaleCompsTab from './PropertyDetailsSaleCompsTab';
import PropertyDetailsSummaryTab from './PropertyDetailsSummaryTab';
import PropertyDetailsTenantsInMarketTab from './PropertyDetailsTenantsInMarketTab';

function PropertyDetails() {
    const { longitude, latitude } = useParams();
    const map = useMap().map;
    const navigate = useNavigate();
    const popupStore = usePopupStore();
    const viewSettingsStore = useViewSettingsStore();
    const contextMenuStore = useContextMenuStore();
    const propertyStore = usePropertyStore();
    const [propertyTab, setPropertyTab] = useState(PropertyTab.Summary);
    const lngLat = useMemo(() => new LngLat(Number(longitude), Number(latitude)), [longitude, latitude]);
    const coordinates = useMemo(() => new GeoCoordinate(lngLat.lat, lngLat.lng), [lngLat]);
    const { property, isLoading, fetchProperty } = useProperty(lngLat);
    const [companyCount, setCompanyCount] = useState(0);
    const [leadCount, setLeadCount] = useState(0);
    const [leaseCompCount, setLeaseCompCount] = useState(0);
    const [dealCount, setDealCount] = useState(0);
    const [saleCompCount, setSaleCompCount] = useState(0);
    const [tenantInMarketCount, setTenantInMarketCount] = useState(0);
    const segmentedButtons = useMemo(() => [
        {
            value: PropertyTab.Summary,
            label: (
                <IconWithTooltip title="Summary">
                    <DetailsIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: PropertyTab.AiGrade,
            label: (
                <IconWithTooltip title="AI">
                    <StarsIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: PropertyTab.Companies,
            label: (
                <IconWithTooltip title="Companies" count={companyCount}>
                    <CompanyIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: PropertyTab.Leads,
            label: (
                <IconWithTooltip title="Leads" count={leadCount}>
                    <LeadIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: PropertyTab.LeaseComps,
            label: (
                <IconWithTooltip title="Lease Comps" count={leaseCompCount}>
                    <LeaseCompIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: PropertyTab.Deals,
            label: (
                <IconWithTooltip title="Deals" count={dealCount}>
                    <DealIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: PropertyTab.SaleComps,
            label: (
                <IconWithTooltip title="Sale Comps" count={saleCompCount}>
                    <SaleCompIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: PropertyTab.TenantsInMarket,
            label: (
                <IconWithTooltip title="Tenants in Market" count={tenantInMarketCount}>
                    <TenantInMarketIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
    ], [companyCount, dealCount, leadCount, leaseCompCount, saleCompCount, tenantInMarketCount]);
    useDetailsShapesOnMap(property?.shapes, "PropertyShape");

    const close = () => navigate("/");
    useClosable(ClosableOrder.Medium, close);

    useEffect(() => {
        popupStore.openPropertyPopup(Number(longitude), Number(latitude));
        return () => popupStore.close();
    }, [longitude, latitude]);

    useEffect(() => {
        if (viewSettingsStore.isMobile)
            fetchProperty();
    }, [longitude, latitude]);

    useEffect(() => {
        if (!map || !property)
            return;

        contextMenuStore.setOnAddParcel(coord => propertyStore.addParcelsToProperty(lngLat, [coord], map), true);
        if (property.parcelIds.length > 1) {
            contextMenuStore.setOnRemoveParcel(
                id => propertyStore.removeParcelFromProperty(lngLat, id),
                coord => propertyStore.findParcelAtCoordinate(lngLat, coord),
            );
        }

        return () => {
            contextMenuStore.clearOnAddParcel();
            contextMenuStore.clearOnRemoveParcel();
        };
    }, [contextMenuStore, propertyStore, lngLat, map, property, popupStore.type]);

    return (
        <>
            {viewSettingsStore.isMobile && isLoading && <OnMapSpinner />}
            {property && (
                <DetailsPanelWithFlyTo type={DetailsType.PropertyDetails} coordinates={coordinates} onClose={close} controls={[
                    <PropertyFavoriteCheckbox key="favorite" id={property.id} className={commonStyles.clickableIcon} />,
                ]}>
                    <div className={detailsStyles.header}>
                        <div className={detailsStyles.headerGroup}>
                            <PropertyIcon color="#25479A" />
                            <div className={detailsStyles.title}>
                                <span className={commonStyles.link} onClick={() => popupStore.openPropertyPopup(property.centerPoint.longitude, property.centerPoint.latitude)}>{property.address}</span>
                            </div>
                        </div>
                        <div className={detailsStyles.headerGroup}>
                            {!viewSettingsStore.isMobile && (
                                <BoeDownloadButton property={property} />
                            )}
                            {!viewSettingsStore.isMobile && (
                                <ButtonWithTooltip title="Add Lead" className={commonStyles.navigationButton} onClick={() => navigate(`/leads/create/${longitude}/${latitude}`)}>
                                    <LeadIcon color="#25479A" />
                                </ButtonWithTooltip>
                            )}
                            {!viewSettingsStore.isMobile && (
                                <ButtonWithTooltip title="Add Deal" className={commonStyles.navigationButton} onClick={() => navigate(`/deals/createFromProperty/${longitude}/${latitude}`)}>
                                    <DealIcon color="#25479A" />
                                </ButtonWithTooltip>
                            )}
                            {!viewSettingsStore.isMobile && (
                                <ButtonWithTooltip title="Add Location" className={commonStyles.navigationButton} onClick={() => navigate(`/locations/create/${longitude}/${latitude}`)}>
                                    <AddLocationIcon color="#25479A" />
                                </ButtonWithTooltip>
                            )}
                        </div>
                    </div>
                    <SegmentedButtons
                        value={propertyTab}
                        onChange={setPropertyTab}
                        options={segmentedButtons}
                        className={detailsStyles.segmentedButtons}
                    />
                    <div className={detailsStyles.body}>
                        {propertyTab === PropertyTab.Summary && <PropertyDetailsSummaryTab />}
                        <PropertyDetailsAiGradeTab visible={propertyTab === PropertyTab.AiGrade} />
                        <PropertyDetailsCompaniesTab visible={propertyTab === PropertyTab.Companies} setEntityCount={setCompanyCount} />
                        <PropertyDetailsLeadsTab visible={propertyTab === PropertyTab.Leads} setEntityCount={setLeadCount} />
                        <PropertyDetailsLeaseCompsTab visible={propertyTab === PropertyTab.LeaseComps} setEntityCount={setLeaseCompCount} />
                        <PropertyDetailsDealsTab visible={propertyTab === PropertyTab.Deals} setEntityCount={setDealCount} />
                        <PropertyDetailsSaleCompsTab visible={propertyTab === PropertyTab.SaleComps} setEntityCount={setSaleCompCount} />
                        <PropertyDetailsTenantsInMarketTab visible={propertyTab === PropertyTab.TenantsInMarket} setEntityCount={setTenantInMarketCount} />
                    </div>
                </DetailsPanelWithFlyTo>
            )}
        </>
    );
}

export default observer(PropertyDetails);

enum PropertyTab {
    Summary,
    AiGrade,
    Companies,
    Leads,
    LeaseComps,
    Deals,
    SaleComps,
    TenantsInMarket,
}

const BoeDownloadButton = observer(function BoeDownloadButton({ property }: IBoeDownloadButtonProps) {
    const { isGenerating, generateBoe } = usePropertyBoeGenerator(property)

    return (
        <ButtonWithTooltip title="Download BOE" className={commonStyles.navigationButton} onClick={() => generateBoe()}>
            {isGenerating ? <Spinner /> : <TableConvertIcon color="#25479A" />}
        </ButtonWithTooltip>
    );
})

interface IBoeDownloadButtonProps {
    property: IProperty;
}
